import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import Modal from "react-bootstrap/Modal";
import { useSelector } from "react-redux";
import CustomSelect from "../../components/Select/Select";
import { Col, Container, Row } from "react-bootstrap";
import Loading from "../../components/Loader/Loading";
import AddBox from "./AddBox";
import "./myVerticalCenterBattleModal.css";
import { Search, X } from "lucide-react";

const MyVerticallyCenteredModal = ({
  setLoading,
  loading,
  setSelected,
  selected,
  handleMinusClick,
  handlePlusClick,
  handleAddToBattleClick,
  onHide,
  show,
  data,
  totalBoxes,
  totalPrice,
}) => {
  const isDarkMode = useSelector((state) => state.theme.darkMode);

  // Local state to persist data by category
  const [dataCache, setDataCache] = useState({});
  const [isChangingCategory, setIsChangingCategory] = useState(false);
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const [currentCategory, setCurrentCategory] = useState(
  selected?.value || "allboxes"
);
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // New filter states
  const [selectedPrice, setSelectedPrice] = useState({
    value: "all",
    label: "All Prices",
  });
  const [selectedOrder, setSelectedOrder] = useState({
    value: "low-to-high",
    label: "Price: Low to High",
  });

  const typeOptions = [
    { value: "allboxes", label: "All Boxes" },
    { value: "Featured", label: "Featured" },
    { value: "Hot", label: "Hot" },
    { value: "Updated", label: "Updated" },
    { value: "Exclusive", label: "Exclusive" },
  ];

  // Price filter options
  const priceOptions = [
    { value: "all", label: "All Prices" },
    { value: "0-100", label: "$0 - $100" },
    { value: "100-250", label: "$100 - $250" },
    { value: "250-500", label: "$250 - $500" },
    { value: "500-1000", label: "$500 - $1000" },
    { value: "1000+", label: "$1000+" },
  ];

  // Order by options
  const orderOptions = [
    { value: "low-to-high", label: "Price: Low to High" },
    { value: "high-to-low", label: "Price: High to Low" },
    { value: "newest", label: "Newest First" },
    { value: "oldest", label: "Oldest First" },
    { value: "name-asc", label: "Name: A-Z" },
    { value: "name-desc", label: "Name: Z-A" },
  ];

  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);
  const modalContentRef = useRef(null);

  // Touch handlers for drag-to-close
  const handleTouchStart = useCallback(
    (e) => {
      if (!isMobile) return;
      dragStartY.current = e.touches[0].clientY;
      setIsDragging(true);
    },
    [isMobile]
  );

  const handleTouchMove = useCallback(
    (e) => {
      if (!isDragging || !isMobile) return;
      const currentY = e.touches[0].clientY;
      const diff = currentY - dragStartY.current;

      // Only allow dragging down (positive diff)
      if (diff > 0) {
        setDragY(diff);
      }
    },
    [isDragging, isMobile]
  );

  const handleTouchEnd = useCallback(() => {
    if (!isMobile) return;
    setIsDragging(false);

    // If dragged more than 150px, close the modal
    if (dragY > 150) {
      onHide();
    }

    // Reset drag position with animation
    setDragY(0);
  }, [dragY, onHide, isMobile]);

  // Update isMobile on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update data cache when we get valid data from props
  useEffect(() => {
    if (data && Array.isArray(data)) {
      const categoryKey = selected?.value || "allboxes";

      setDataCache((prevCache) => ({
        ...prevCache,
        [categoryKey]: data,
      }));

      setCurrentCategory(categoryKey);
      setIsChangingCategory(false);
    }
  }, [data, selected?.value]);

  // Reset changing state when loading completes
  useEffect(() => {
    if (!loading) {
      setIsChangingCategory(false);
    }
  }, [loading]);

  // Debounced category change handler
  const handleChange = useCallback(
    (selectedOption) => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }

      if (selectedOption.value === currentCategory) {
        return;
      }

      setIsChangingCategory(true);

      const timeout = setTimeout(() => {
        setSelected(selectedOption);
        setCurrentCategory(selectedOption.value);

        if (setLoading) {
          setLoading(true);
        }
      }, 300);

      setDebounceTimeout(timeout);
    },
    [setSelected, setLoading, debounceTimeout, currentCategory]
  );

  // Price filter handler
  const handlePriceChange = useCallback((selectedOption) => {
    setSelectedPrice(selectedOption);
  }, []);

  // Order by handler
  const handleOrderChange = useCallback((selectedOption) => {
    setSelectedOrder(selectedOption);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
    };
  }, [debounceTimeout]);

  // Get data for current category
  const getCurrentCategoryData = useMemo(() => {
    const categoryKey = selected?.value || "allboxes";

    if (loading || isChangingCategory) {
      if (dataCache[categoryKey]) {
        return dataCache[categoryKey];
      }

      const availableCategories = Object.keys(dataCache);
      if (availableCategories.length > 0) {
        return dataCache[availableCategories[0]];
      }

      return [];
    }

    return dataCache[categoryKey] || [];
  }, [selected?.value, dataCache, loading, isChangingCategory]);

  // Apply filtering and sorting
  const filteredData = useMemo(() => {
    let currentData = [...getCurrentCategoryData];

    // Apply category filter
    if (selected && selected.value !== "allboxes") {
      currentData = currentData.filter((item) => {
        return (
          !item?.box_category?.name ||
          item?.box_category?.name === selected.value
        );
      });
    }

    // Apply search filter
    if (search.trim()) {
      const searchLower = search.toLowerCase().trim();
      currentData = currentData.filter((item) => {
        const name = item?.name || item?.title || "";
        return name.toLowerCase().includes(searchLower);
      });
    }

    // Apply price filter
    if (selectedPrice && selectedPrice.value !== "all") {
      currentData = currentData.filter((item) => {
        const price = parseFloat(item?.box_price) || 0;
        switch (selectedPrice.value) {
          case "0-100":
            return price >= 0 && price <= 100;
          case "100-250":
            return price > 100 && price <= 250;
          case "250-500":
            return price > 250 && price <= 500;
          case "500-1000":
            return price > 500 && price <= 1000;
          case "1000+":
            return price > 1000;
          default:
            return true;
        }
      });
    }

    // Apply sorting
    if (selectedOrder) {
      currentData.sort((a, b) => {
        switch (selectedOrder.value) {
          case "low-to-high":
            return (
              (parseFloat(a?.box_price) || 0) -
              (parseFloat(b?.box_price) || 0)
            );
          case "high-to-low":
            return (
              (parseFloat(b?.box_price) || 0) -
              (parseFloat(a?.box_price) || 0)
            );
          case "newest":
            return (
              new Date(b?.created_at || 0) - new Date(a?.created_at || 0)
            );
          case "oldest":
            return (
              new Date(a?.created_at || 0) - new Date(b?.created_at || 0)
            );
          case "name-asc":
            return (a?.name || a?.title || "").localeCompare(
              b?.name || b?.title || ""
            );
          case "name-desc":
            return (b?.name || b?.title || "").localeCompare(
              a?.name || a?.title || ""
            );
          default:
            return 0;
        }
      });
    }

    return currentData;
  }, [getCurrentCategoryData, selected, search, selectedPrice, selectedOrder]);

  const isLoading = loading || isChangingCategory;

  return (
    <div
      ref={modalContentRef}
      style={{
        transform: isMobile ? `translateY(${dragY}px)` : "none",
        transition: isDragging ? "none" : "transform 0.9s ease-out",
      }}
    >
      <Modal
        onHide={onHide}
        show={show}
        size="xl"
        aria-labelledby="contained-modal-title-vcenter"
        centered={!isMobile}
        className={`beautiful-modal ${
          isDarkMode ? "dark-theme" : "light-theme"
        } ${isMobile ? "mobile-bottom-sheet" : ""}`}
        backdrop="static"
        keyboard={false}
        dialogClassName={isMobile ? "modal-bottom-sheet" : ""}
      >
        {isMobile && (
          <div
            className="drag-handle-container"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="drag-handle" />
          </div>
        )}

        {/* HEADER - Search Bar */}
        <Modal.Header
          className={`modal-header-enhanced p-4 ${
            isDarkMode ? "header-dark" : "modal-header-light"
          }`}
        >
          <div className="text-white">Add Boxes</div>
        </Modal.Header>

        {/* BODY - Contains sticky filter + scrollable grid */}
        <Modal.Body
          className={`modal-body-enhanced ${
            isDarkMode ? "battle-model-darkMode body-dark" : "body-light"
          }`}
        >
          <div className="header-content  p-3 p-lg-2">
            <div style={{ width: "100%", position: "relative" }}>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search"
                className={`flex h-[56px] px-5 py-2 items-center gap-2 flex-1 search-text-inputs ${
                  isDarkMode
                    ? "search-text-inputs-dark"
                    : "search-text-inputs-light"
                }`}
              />
              <Search size={19} className="search_Icon" />
            </div>
          </div>
          {/* 
            CATEGORY FILTER SECTION
            Desktop: sticky (stays fixed, doesn't scroll)
            Mobile: normal flow (scrolls with content) 
          */}
          <div
            className={`category-filter-sticky-wrapper ${
              isDarkMode ? "category-display-dark" : "category-display-light"
            }`}
          >
            <div className="category-display">
              <div className="filter-section">
                <div className="select-with-label">
                  <CustomSelect
                    options={typeOptions}
                    onChange={handleChange}
                    value={
                      selected || { value: "allboxes", label: "All Boxes" }
                    }
                    label="Type" // 👈
                    isDisabled={isLoading}
                  />
                </div>
              </div>

              <div className="filter-section">
                <div className="select-with-label">
                  <CustomSelect
                    options={priceOptions}
                    onChange={handlePriceChange}
                    value={selectedPrice}
                    label="Filter" // 👈
                    isDisabled={isLoading}
                  />
                </div>
              </div>

              <div className="filter-section">
                <div className="select-with-label">
                  <CustomSelect
                    options={orderOptions}
                    onChange={handleOrderChange}
                    value={selectedOrder}
                    label="Sort" // 👈
                    isDisabled={isLoading}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* SCROLLABLE AREA - Only this part scrolls on desktop */}
          <div className="modal-body-scroll-area">
            {/* Loading State */}
            {isLoading && (
              <div className="loading-overlay">
                <div className="loading-content">
                  <Loading />
                  <p
                    className={`loading-text ${
                      isDarkMode ? "text-dark" : "text-light"
                    }`}
                  >
                    {isChangingCategory
                      ? `Switching to ${selected?.label || "category"}...`
                      : "Loading boxes..."}
                  </p>
                </div>
              </div>
            )}

            {/* Content Grid */}
            {!isLoading && (
              <div className="content-grid">
                <Container fluid>
                  <Row className="boxes-grid">
                    {filteredData && filteredData.length > 0 ? (
                      filteredData.map((item) => (
                        <Col
                          xl={3}
                          lg={3}
                          md={6}
                          sm={6}
                          xs={6}
                          key={item.id}
                          className="box-column"
                        >
                          <div className="box-wrapper">
                            <AddBox
                              item={item}
                              isDarkMode={isDarkMode}
                              handleAddToBattleClick={handleAddToBattleClick}
                              handlePlusClick={handlePlusClick}
                              handleMinusClick={handleMinusClick}
                              imageContainerClass="image-container--modal"
                            />
                          </div>
                        </Col>
                      ))
                    ) : (
                      <div className="empty-state">
                        <div
                          className={`empty-content ${
                            isDarkMode ? "empty-dark" : "empty-light"
                          }`}
                        >
                          <div className="empty-icon">📭</div>
                          <h5>No boxes found</h5>
                          <p>
                            {search
                              ? `No boxes found matching "${search}". Try a different search term.`
                              : selected && selected.value !== "allboxes"
                                ? `No boxes found in "${selected.label}" category. Try selecting a different category.`
                                : "No boxes available at the moment. Please try again later."}
                          </p>
                        </div>
                      </div>
                    )}
                  </Row>
                </Container>
              </div>
            )}
          </div>
        </Modal.Body>

        {/* Footer */}
        <div
          className={`modal-footer-enhanced ${
            isDarkMode ? "footer-dark" : "footer-light"
          }`}
        >
          <div className="footer-content">
            <div className="battle-summary">
              <div className="summary-stats">
                <div
                  className={`stat-item ${
                    isDarkMode ? "stat-dark" : "stat-light"
                  }`}
                >
                  <div
                    className={`stat-label-pill ${
                      isDarkMode
                        ? "stat-label-pill-dark"
                        : "stat-label-pill-light"
                    }`}
                  >
                    Value:{" "}
                    <span
                      className={
                        isDarkMode
                          ? "stat-label-pill-price-dark"
                          : "stat-label-pill-price-light"
                      }
                    >
                      ${totalPrice.toFixed(2)}
                    </span>
                  </div>
                </div>
                <div
                  className={`stat-item ${
                    isDarkMode ? "stat-dark" : "stat-light"
                  }`}
                >
                  <div
                    className={`stat-label-pill ${
                      isDarkMode
                        ? "stat-label-pill-dark"
                        : "stat-label-pill-light"
                    }`}
                  >
                    Boxes:
                    <span
                      className={
                        isDarkMode
                          ? "stat-label-pill-price-dark"
                          : "stat-label-pill-price-light"
                      }
                    >
                      {" "}
                      {totalBoxes}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="footer-actions">
               <button onClick={onHide} style={{padding:"6.667px 28px"}} className="deep-primary-monochrome-btn-dark width-crt-btn ">
                  Cancel
                </button>
              <button
                onClick={onHide}
                className={`action-button done-button ${
                  isDarkMode ? "button-dark" : "button-light"
                }`}
                disabled={isLoading}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
        {isMobile && (
          <div
            className={` ${isDarkMode ? "swipe-handle-container-bottom-bm" : "swipe-handle-container-bottom-bm-light"} `}
          >
            <div className="swipe-handle-bottom-bm" />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MyVerticallyCenteredModal;
